import React from "react";
import { AlertTriangle, Copy, RotateCcw } from "lucide-react";

/**
 * Top-level error boundary.
 *
 * Catches any render-time error thrown by descendants and renders a clean
 * recovery surface instead of the raw React error overlay. This is our
 * insurance against a missed `formatApiError()` adoption — even if a raw
 * 422 detail array sneaks into JSX somewhere, the user sees a calm error
 * card with a "Copy details" button rather than a red runtime screen.
 *
 * Class component because React still requires class-based boundaries
 * (the hooks API has no equivalent of componentDidCatch).
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null, copied: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Surface to dev console so we can grep it in browser devtools.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] caught render error:", error, info);
    this.setState({ info });
  }

  handleCopy = async () => {
    const { error, info } = this.state;
    const payload = [
      `Error: ${error?.message || String(error)}`,
      `URL: ${window.location.href}`,
      `Time: ${new Date().toISOString()}`,
      `User-Agent: ${navigator.userAgent}`,
      "",
      "Stack:",
      error?.stack || "(no stack)",
      "",
      "Component stack:",
      info?.componentStack || "(no component stack)",
    ].join("\n");
    try {
      await navigator.clipboard.writeText(payload);
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    } catch {
      // Clipboard blocked — fall back to a textarea select.
      const ta = document.createElement("textarea");
      ta.value = payload;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    }
  };

  handleReload = () => {
    // Reset state first in case the error was transient and the same URL
    // would now succeed (e.g. backend returned 502 once).
    this.setState({ error: null, info: null });
    window.location.reload();
  };

  render() {
    const { error, copied } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        data-testid="error-boundary"
        className="flex min-h-screen items-center justify-center bg-slate-50 p-6"
      >
        <div className="w-full max-w-md rounded-2xl border border-cargodash-alert-600/20 bg-white p-8 shadow-lg">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-cargodash-alert-600/10">
            <AlertTriangle className="h-6 w-6 text-cargodash-alert-600" />
          </div>
          <h1 className="mt-5 font-display text-xl font-semibold tracking-tight text-slate-900">
            Something went wrong
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            The page hit an unexpected error. Try refreshing — if it keeps
            happening, copy the error details and send them to support.
          </p>

          <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
            <code className="block break-all font-mono text-[11px] text-slate-600">
              {error?.message || String(error)}
            </code>
          </div>

          <div className="mt-6 flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={this.handleReload}
              data-testid="error-boundary-reload"
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-md bg-cargodash-primary-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-cargodash-primary-700"
            >
              <RotateCcw className="h-4 w-4" />
              Refresh page
            </button>
            <button
              type="button"
              onClick={this.handleCopy}
              data-testid="error-boundary-copy"
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              <Copy className="h-4 w-4" />
              {copied ? "Copied" : "Copy details"}
            </button>
          </div>
        </div>
      </div>
    );
  }
}
